/*
 * drawer-builder-devlines.js
 * --------------------------------------------------------------------------
 * Throwaway DEV overlay for the drawer builder page. Draws labelled, dashed
 * outlines ("dev lines") around the major UI regions so the current layout
 * can be screenshotted and discussed precisely — in both the COLLAPSED and
 * EXPANDED states of the products panel.
 *
 * SAFE BY DEFAULT: does nothing unless explicitly switched on. It never
 * blocks interaction (everything is pointer-events:none) and ships no styles
 * until activated, so leaving it referenced in the theme has no customer
 * impact.
 *
 * Activate:   add ?devlines=1 to the URL (persists for the session)
 * Deactivate: add ?devlines=0  (clears the flag)
 * Toggle:     press  d  then  l  (quickly) while on the page
 *
 * This file is intentionally standalone — no dependencies on the legacy
 * IIFE, jQuery, or the ES modules. Remove the <script> tag + this file to
 * delete it cleanly.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'md-devlines';

  // ---- activation -----------------------------------------------------------
  // URL-param only — the overlay no longer persists, so a normal page open
  // NEVER shows it. Wipe any flag persisted by older versions.
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  var params = new URLSearchParams(window.location.search);
  var paramOn = params.has('devlines') && !/^(0|off|false)$/i.test(params.get('devlines') || '');
  function isOn() { return paramOn; }

  // ---- region map -----------------------------------------------------------
  // Each entry: a CSS selector + human label. Order = badge number (1-based).
  // Colours are chosen to stay distinct where regions overlap/nest.
  var REGIONS = [
    { sel: '.db-topbar',                  label: 'Top bar (L/W size controls)', color: '#ff3b30' },
    { sel: '.drawerBundle',               label: 'Drawer build grid',           color: '#34c759' },
    { sel: '.db-expand-tab',              label: 'Expand / Collapse tab',       color: '#ff9500' },
    { sel: '.db-sheet-grabber',           label: 'Mobile grabber',              color: '#ff9500' },
    { sel: '.drawerOptions',              label: 'Products panel (sidebar/sheet)', color: '#007aff' },
    { sel: '.addCartMb',                  label: 'Total + Add to cart',         color: '#af52de' },
    { sel: '.product-filter-select-wrapper', label: 'Filter block',             color: '#5856d6' },
    { sel: '#prd-3',                      label: 'Filter: Type',                color: '#00c7be' },
    { sel: '#prd-1',                      label: 'Filter: Size',                color: '#00c7be' },
    { sel: '#prd-2',                      label: 'Filter: Utensil',             color: '#00c7be' },
    { sel: '.db-clear-all-filters',       label: 'Clear all filters',           color: '#ff2d55' },
    { sel: '.db-chip-rail',               label: 'Mobile filter chip rail',     color: '#5856d6' },
    { sel: '.variantData',                label: 'Colour swatches',             color: '#ffcc00' },
    { sel: '#builder-products',           label: 'Product list (modules)',      color: '#32ade6' }
  ];

  var overlay = null;       // fixed-position layer holding all boxes
  var badge = null;         // top-centre state badge
  var legend = null;        // bottom-left legend
  var legendOpen = false;   // key hidden by default so it doesn't cover content
  var rafPending = false;

  function ensureStyles() {
    if (document.getElementById('md-devlines-style')) return;
    var css = ''
      + '#md-devlines-overlay{position:fixed;inset:0;z-index:2147483600;pointer-events:none;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;}'
      + '.md-dl-box{position:fixed;box-sizing:border-box;border:2px dashed var(--c,#0a84ff);border-radius:4px;pointer-events:none;}'
      + '.md-dl-tag{position:absolute;top:-1px;left:-1px;transform:translateY(-100%);background:var(--c,#0a84ff);color:#fff;font-size:11px;line-height:1;font-weight:700;padding:3px 6px;border-radius:4px 4px 4px 0;white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,.4);}'
      + '.md-dl-tag.is-inside{transform:none;top:1px;left:1px;border-radius:0 0 4px 0;}'
      + '#md-devlines-badge{position:fixed;top:8px;left:50%;transform:translateX(-50%);z-index:2147483601;pointer-events:auto;cursor:pointer;background:#111;color:#fff;font:700 12px/1 ui-monospace,Menlo,monospace;padding:6px 12px;border-radius:999px;box-shadow:0 2px 8px rgba(0,0,0,.5);letter-spacing:.04em;}'
      + '#md-devlines-badge .st{color:#34c759;}'
      + '#md-devlines-legend{position:fixed;bottom:8px;left:8px;z-index:2147483601;pointer-events:none;background:rgba(17,17,17,.92);color:#fff;font:600 11px/1.5 ui-monospace,Menlo,monospace;padding:8px 10px;border-radius:6px;max-width:240px;box-shadow:0 2px 8px rgba(0,0,0,.5);}'
      + '#md-devlines-legend b{display:block;margin-bottom:4px;font-size:11px;letter-spacing:.04em;color:#ffd60a;}'
      + '#md-devlines-legend .row{display:flex;gap:6px;align-items:baseline;opacity:.95;}'
      + '#md-devlines-legend .n{display:inline-block;min-width:16px;text-align:center;color:#000;border-radius:3px;font-weight:800;}'
      + '#md-devlines-legend .miss{opacity:.4;text-decoration:line-through;}';
    var s = document.createElement('style');
    s.id = 'md-devlines-style';
    s.textContent = css;
    document.head.appendChild(s);
  }

  function isVisible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return false;
    var cs = getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0';
  }

  function build() {
    ensureStyles();

    overlay = document.createElement('div');
    overlay.id = 'md-devlines-overlay';

    badge = document.createElement('div');
    badge.id = 'md-devlines-badge';
    // The badge is the one interactive element — tap it to show/hide the key
    // so it stops covering the colour/filter area in screenshots.
    badge.addEventListener('click', function () {
      legendOpen = !legendOpen;
      legend.style.display = legendOpen ? '' : 'none';
    });

    legend = document.createElement('div');
    legend.id = 'md-devlines-legend';
    legend.style.display = 'none'; // hidden by default — clean screenshots

    document.body.appendChild(overlay);
    document.body.appendChild(badge);
    document.body.appendChild(legend);

    // keep boxes glued to elements as things move/scroll/resize
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    var mo = new MutationObserver(schedule);
    mo.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['class', 'style'] });

    render();
  }

  function schedule() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () { rafPending = false; render(); });
  }

  function render() {
    if (!overlay) return;
    overlay.innerHTML = '';

    var expanded = document.body.classList.contains('products-expanded');
    badge.innerHTML = 'DEV LINES · <span class="st">' + (expanded ? 'EXPANDED' : 'COLLAPSED') + '</span> · ' + (legendOpen ? 'hide key' : 'tap for key');
    legend.style.display = legendOpen ? '' : 'none';

    var legendRows = '<b>DEV LINES — ' + (expanded ? 'EXPANDED' : 'COLLAPSED') + '</b>';

    REGIONS.forEach(function (region, i) {
      var n = i + 1;
      var el = document.querySelector(region.sel);
      var visible = isVisible(el);

      legendRows += '<div class="row' + (visible ? '' : ' miss') + '">'
        + '<span class="n" style="background:' + region.color + ';color:#fff;">' + n + '</span>'
        + '<span>' + region.label + '</span></div>';

      if (!visible) return;

      var r = el.getBoundingClientRect();
      var box = document.createElement('div');
      box.className = 'md-dl-box';
      box.style.setProperty('--c', region.color);
      box.style.left = r.left + 'px';
      box.style.top = r.top + 'px';
      box.style.width = r.width + 'px';
      box.style.height = r.height + 'px';

      var tag = document.createElement('span');
      tag.className = 'md-dl-tag' + (r.top < 22 ? ' is-inside' : '');
      tag.style.setProperty('background', region.color);
      tag.textContent = n + '  ' + region.label;
      box.appendChild(tag);

      overlay.appendChild(box);
    });

    legend.innerHTML = legendRows;
  }

  function teardown() {
    [overlay, badge, legend].forEach(function (n) { if (n && n.parentNode) n.parentNode.removeChild(n); });
    overlay = badge = legend = null;
  }

  // ---- keyboard toggle:  d then l  (in-session only, no persistence) --------
  var lastKey = '';
  document.addEventListener('keydown', function (e) {
    if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
    if (e.key === 'l' && lastKey === 'd') {
      if (overlay) teardown(); else build();
    }
    lastKey = e.key;
  });

  // ---- boot -----------------------------------------------------------------
  function boot() { if (isOn() && !overlay) build(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
